<template>
  <div ref="container" class="w-full relative">
    <svg
      ref="svg"
      :width="width"
      :height="height"
      id="arrow-plot"
      class="text-gray-800"
    />
    <VTooltip :show="tooltip.show" :target="tooltip.target" class="z-30">
      <div
        class="bg-black bg-opacity-80 text-white px-4 py-2 rounded-lg text-xs flex items-center"
      >
        <div class="">
          <div>
            En Abril había un
            <span class="font-bold">{{ tooltip.pre }}%</span> mientras que en
            Diciembre había un
            <span class="font-bold">{{ tooltip.post }}%</span>
          </div>
        </div>
      </div>
    </VTooltip>
  </div>
</template>

<script>
import * as d3 from "d3";
import VTooltip from "@/components/VTooltip";

export default {
  name: "ArrowPlot",
  components: {
    VTooltip,
  },
  props: {
    data: {
      type: Array,
      default: () => [],
    },
    colorArrowPositive: {
      type: String,
      default: "#0a5fc8", // blue
    },
    colorArrowNegative: {
      type: String,
      default: "#ff3c37", // red
    },
    colorText: {
      type: String,
      default: "#1F2937", // gray-800
    },
    colorAxis: {
      type: String,
      default: "#6B7280", // gray-500
    },
    xAxisDomain: {
      type: Array,
      default: () => [0, 100],
    },
    legendLabels: {
      type: Array,
      default: () => [2018, 2021],
    },
  },
  data() {
    return {
      width: 600,
      height: 250,
      margin: { top: 15, right: 30, bottom: 20, left: 330 },
      maxLabelHeight: 0,
      tooltip: {
        show: false,
        target: null,
        pre: 0,
        post: 0,
      },
    };
  },
  computed: {
    xScale() {
      return d3
        .scaleLinear()
        .domain(this.xAxisDomain)
        .range([0, this.width - this.margin.left - this.margin.right])
        .nice();
    },
    yScale() {
      return d3
        .scaleBand()
        .domain(this.data.map((d) => d.label))
        .range([0, this.height - this.margin.top - this.margin.bottom])
        .padding(1);
    },
  },
  methods: {
    createYAxis() {
      d3.select(this.$refs.svg)
        .select("#chart")
        .remove();

      const svg = d3.select(this.$refs.svg);

      const chart = svg
        .append("g")
        .attr("id", "chart")
        .attr(
          "transform",
          `translate(${this.margin.left}, ${this.margin.top})`
        );

      const yAxis = chart
        .append("g")
        .attr("id", "y-axis")
        .call(
          d3
            .axisLeft(this.yScale)
            .tickSize(this.width - this.margin.left - this.margin.right + 45)
        )
        .attr(
          "transform",
          `translate(${this.width -
            this.margin.left -
            this.margin.right +
            12}, 0)`
        );
      yAxis.select(".domain").remove();
      yAxis
        .selectAll(".tick line")
        .attr("stroke", this.colorAxis)
        .attr("stroke-dasharray", "2,2")
        .attr("stroke-opacity", 0.2);
      yAxis
        .selectAll(".tick text")
        .attr("font-size", "0.875rem")
        .attr("fill", this.colorText);

      // wrap labels
      yAxis.selectAll(".tick text").call(this.wrap);

      yAxis.attr("font-family", null);

      // set size again to adjust height
      this.setSize();
    },
    createChart() {
      const svg = d3.select(this.$refs.svg);

      const chart = svg.select("#chart");

      // create x axis
      const xAxis = chart
        .append("g")
        .attr("id", "x-axis")
        .call(
          d3
            .axisBottom(this.xScale)
            .tickSize(
              this.height -
                this.margin.top -
                this.margin.bottom -
                this.height * 0.1
            )
            .ticks(4)
            .tickFormat((d) => `${d}%`)
        )
        .attr("transform", `translate(0, ${(this.height * 0.1) / 2})`);

      xAxis.select(".domain").remove();

      xAxis
        .selectAll(".tick line")
        .attr("stroke-opacity", 0.3)
        .attr("stroke", this.colorAxis);

      xAxis.selectAll(".tick text").attr("fill", this.colorAxis);

      // create labels
      const labels = chart
        .append("g")
        .attr("id", "labels")
        .attr("font-size", "0.625rem")
        .attr("fill", this.colorAxis)
        .selectAll("text")
        .data(this.data)
        .join("text")
        .attr("transform", (d) => `translate(0, ${this.yScale(d.label)})`);

      // x1
      labels
        .append(`tspan`)
        .attr("text-anchor", (d) => (d.x1 < d.x2 ? "end" : "start"))
        .attr("alignment-baseline", "central")
        .attr("x", (d) => {
          if (!d.x1 && !d.x2) return this.xScale(d.x1);
          return d.x1 < d.x2 ? this.xScale(d.x1) - 8 : this.xScale(d.x1) + 8;
        })
        .text((d) => d.x1);

      // x2
      labels
        .append(`tspan`)
        .attr("text-anchor", (d) => (d.x2 < d.x1 ? "end" : "start"))
        .attr("alignment-baseline", "central")
        .attr("x", (d) => {
          if (!d.x1 && !d.x2) return this.xScale(d.x1);
          return d.x2 < d.x1 ? this.xScale(d.x2) - 8 : this.xScale(d.x2) + 8;
        })
        .text((d) => d.x2);

      // create arrows
      const arrows = chart
        .append("g")
        .attr("id", "arrows")
        .selectAll("g")
        .data(this.data)
        .join("g")
        .attr("class", "arrow");

      const lines = arrows
        .append("line")
        .attr("stroke-linecap", "round")
        .attr("x1", (d) => this.xScale(d.x1))
        .attr("y1", (d) => this.yScale(d.label))
        .attr("x2", (d) => this.xScale(d.x1))
        .attr("y2", (d) => this.yScale(d.label))
        .attr("stroke", (d) =>
          +d.x2 > +d.x1 ? this.colorArrowPositive : this.colorArrowNegative
        )
        .attr("stroke-width", (d) => (+d.x2 === +d.x1 ? 0 : 2.3));

      // add transition
      const t = d3.transition().duration(750);
      lines.transition(t).attr("x2", (d) => this.xScale(d.x2));

      // add arrow heads
      const arrowHeads = arrows
        .append("polyline")
        .attr("stroke", (d) =>
          +d.x2 > +d.x1 ? this.colorArrowPositive : this.colorArrowNegative
        )
        .attr("stroke-width", (d) => (+d.x2 === +d.x1 ? 0 : 2.1))
        .attr("stroke-linecap", "round")
        .attr("stroke-linejoin", "round")
        .attr("fill", "none")
        .attr("points", (d) => {
          const x = this.xScale(d.x1);
          const y = this.yScale(d.label);
          const points =
            +d.x2 > +d.x1
              ? `${x - 4}, ${y - 4.5} ${x + 0.5}, ${y} ${x - 4}, ${y + 4.5}`
              : `${x + 4}, ${y - 4.5} ${x - 0.5}, ${y} ${x + 4}, ${y + 4.5}`;

          return points;
        });

      arrowHeads.transition(t).attr("points", (d) => {
        const x = this.xScale(d.x2);
        const y = this.yScale(d.label);
        const points =
          +d.x2 > +d.x1
            ? `${x - 4}, ${y - 4.5} ${x + 0.5}, ${y} ${x - 4}, ${y + 4.5}`
            : `${x + 4}, ${y - 4.5} ${x - 0.5}, ${y} ${x + 4}, ${y + 4.5}`;

        return points;
      });

      // create legend
      if (this.data.length) {
        const legend = chart
          .append("g")
          .attr("id", "legend")
          .attr("fill", this.colorAxis);

        // lines
        legend
          .append("line")
          .attr("x1", this.xScale(this.data[0].x1))
          .attr("x2", this.xScale(this.data[0].x1))
          .attr("y1", this.yScale(this.data[0].label) - 7)
          .attr("y2", this.yScale(this.data[0].label) - 14)
          .attr("stroke", this.colorAxis)
          .attr("stroke-width", 1);

        legend
          .append("line")
          .attr("x1", this.xScale(this.data[0].x2))
          .attr("x2", this.xScale(this.data[0].x2))
          .attr("y1", this.yScale(this.data[0].label) - 7)
          .attr("y2", this.yScale(this.data[0].label) - 14)
          .attr("stroke", this.colorAxis)
          .attr("stroke-width", 1);

        // categories
        legend
          .append("text")
          .attr("font-size", "0.75rem")
          .attr(
            "text-anchor",
            this.data[0].x1 < this.data[0].x2 ? "end" : "start"
          )
          .attr("x", this.xScale(this.data[0].x1))
          .attr("y", this.yScale(this.data[0].label) - 16)
          .text(this.legendLabels[0]);

        legend
          .append("text")
          .attr("font-size", "0.75rem")
          .attr(
            "text-anchor",
            this.data[0].x2 < this.data[0].x1 ? "end" : "start"
          )
          .attr("x", this.xScale(this.data[0].x2))
          .attr("y", this.yScale(this.data[0].label) - 16)
          .text(this.legendLabels[1]);
      }

      // add rectangle for hover area
      const rects = arrows
        .append("rect")
        .attr("x", (d) =>
          d.x1 < d.x2 ? this.xScale(d.x1) - 30 : this.xScale(d.x2) - 30
        )
        .attr("y", (d) => this.yScale(d.label) - 10)
        .attr(
          "width",
          (d) => Math.abs(this.xScale(d.x1) - this.xScale(d.x2)) + 60
        )
        .attr("height", 20)
        .attr("fill", "transparent");

      // add tooltip
      rects
        .on("mouseover", (e, d) => {
          this.showTooltip(e, d);
        })
        .on("mouseout", () => {
          this.hideTooltip();
        });
    },
    setSize() {
      const container = this.$refs.container;
      this.width = container.clientWidth;

      // reserve part of total space for labels
      this.margin.left =
        this.width > 700 ? this.width / 4 : (this.width * 2) / 5;

      // set height based on number and height of labels
      this.height =
        this.margin.top +
        this.margin.bottom +
        this.data.length * (this.maxLabelHeight + 25);
    },
    resize() {
      this.setSize();
      this.createYAxis();
      this.createChart();
    },
    removeResizeObserver() {
      if (ResizeObserver) {
        this.resizeObserver.unobserve(this.$refs.container);
      } else {
        // for IE
        window.removeEventListener("resize", this.resize);
      }
    },
    wrap(labels) {
      // reset max label height to adjust chart height
      this.maxLabelHeight = 0;
      // labels is a d3 selection of all text nodes
      labels
        // convert selection to array
        .nodes()
        // loop over each text node
        .forEach((node) => {
          // select text element and store x attribute
          const textElement = d3.select(node);
          const x = textElement.attr("x");
          // store label text
          const label = textElement.text();
          // split label into words
          let words = label.split(" ");
          // empty text and append tspan
          let tspan = textElement
            .text("")
            .append("tspan")
            .attr("x", x);

          // add words to tspan until length exceeds space for left margin
          let line = [];

          words.forEach((word) => {
            // add word to line
            line.push(word);
            // add line to tspan
            tspan.text(line.join(" "));
            // check if length tspan exceeds space for left margin
            if (tspan.node().getComputedTextLength() > this.margin.left - 40) {
              // remove last word and add shorter line to tspan
              line.pop();
              tspan.text(line.join(" "));

              // append new tspan
              tspan = textElement
                .append("tspan")
                .attr("x", x)
                .attr("dy", "1em");
              // reset line with current word
              line = [word];
              // add word to tspan
              tspan.text(word);
            }
          });

          // position tspans
          const tspans = textElement.selectAll("tspan");

          if (tspans.size() > 1) {
            tspans.attr("dy", (d, i) =>
              i ? "1em" : `-${tspans.size() / 2 - 1}em`
            );
          }

          const height = textElement.node().getBBox().height;
          if (this.maxLabelHeight < height) this.maxLabelHeight = height;
        });
    },
    showTooltip({ target }, d) {
      this.tooltip.target = target;
      this.tooltip.pre = d.x1;
      this.tooltip.post = d.x2;
      this.tooltip.show = true;
    },
    hideTooltip() {
      this.tooltip.show = false;
    },
  },
  mounted() {
    this.setSize();
    this.createYAxis();
    this.createChart();

    if (ResizeObserver) {
      this.resizeObserver = new ResizeObserver(() => {
        this.resize();
      });
      this.resizeObserver.observe(this.$refs.container);
    } else {
      // for IE
      window.addEventListener("resize", this.resize);
    }
  },
  beforeDestroy() {
    this.removeResizeObserver();
  },
  watch: {
    data() {
      this.setSize();
      this.createYAxis();
      this.createChart();
    },
    height() {
      this.createYAxis();
      this.createChart();
    },
  },
};
</script>

<style scoped>
#arrow-plot {
  overflow: visible !important;
}
</style>
